import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { useAuth } from '../../../contexts/auth/AuthContext';
import { USER_PERMISSION_GROUPS } from '../../Navigation/constants/NavigationConfig';
import apiClient from '../../../services/apiClient';

const PAGE_SIZE = 20;
const ASSIGNABLE_PERMISSIONS = new Set(USER_PERMISSION_GROUPS.flatMap(group => (
  group.permissions.map(permission => permission.key)
)));

const formatDate = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString();
};

const PermissionControls = ({ user, disabled, onToggle }) => {
  if (user.role === 'admin') {
    return <p className="text-sm text-text-secondary">All permissions included with the admin role.</p>;
  }

  const permissions = user.permissions || [];
  const hasFullOpsAccess = permissions.includes('ops_access');

  return (
    <div className="grid gap-3 xl:grid-cols-2">
      {USER_PERMISSION_GROUPS.map(group => (
        <fieldset key={group.id} className="rounded-lg border border-glass-border p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-text-secondary">
            {group.label}
          </legend>
          <div className="grid gap-1 sm:grid-cols-2">
            {group.permissions.map(permission => {
              const inherited = group.id === 'operations'
                && permission.key !== 'ops_access'
                && hasFullOpsAccess;
              const checked = inherited || permissions.includes(permission.key);
              return (
                <label
                  key={permission.key}
                  className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm text-text-secondary hover:bg-surface-2"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggle(user, permission.key)}
                    disabled={disabled || inherited}
                    className="h-5 w-5 accent-accent disabled:cursor-not-allowed"
                  />
                  <span className={inherited ? 'text-text-tertiary' : ''}>{permission.label}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
    </div>
  );
};

const StatusControl = ({ user, currentUserId, disabled, onChange }) => {
  const isSelf = String(user._id) === String(currentUserId || '');
  return (
    <button
      type="button"
      onClick={() => onChange(user)}
      disabled={disabled || (isSelf && user.isActive)}
      className={`min-h-11 rounded-lg px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
        user.isActive
          ? 'border border-red-400/50 text-red-300 hover:bg-red-400/10'
          : 'bg-accent text-black hover:bg-accent-hover'
      }`}
      aria-label={`${user.isActive ? 'Deactivate' : 'Activate'} ${user.username}`}
      title={isSelf && user.isActive ? 'You cannot deactivate your own account' : undefined}
    >
      {user.isActive ? 'Deactivate' : 'Activate'}
    </button>
  );
};

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [newUser, setNewUser] = useState({ username: '', password: '' });
  const [creating, setCreating] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [mutatingUsers, setMutatingUsers] = useState({});

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setLoadError('');
    try {
      const response = await apiClient.get('/users', {
        params: { page, limit: PAGE_SIZE, search: search || undefined }
      });
      const payload = response.data;
      if (Array.isArray(payload)) {
        setUsers(payload);
        setPagination({ page: 1, totalPages: 1, total: payload.length });
      } else {
        setUsers(Array.isArray(payload?.users) ? payload.users : []);
        setPagination(payload?.pagination || { page, totalPages: 1, total: 0 });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoadError(error.response?.data?.message || 'Unable to load users.');
    } finally {
      setLoadingUsers(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const replaceUser = (updatedUser) => {
    setUsers(current => current.map(user => (
      user._id === updatedUser._id ? { ...user, ...updatedUser } : user
    )));
  };

  const setRowMutation = (userId, value) => {
    setMutatingUsers(current => ({ ...current, [userId]: value }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setCreating(true);
    try {
      await apiClient.post('/users/create', newUser);
      toast.success('User added successfully');
      setNewUser({ username: '', password: '' });
      if (page !== 1) setPage(1);
      else await fetchUsers();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.message || 'Failed to add user');
    } finally {
      setCreating(false);
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const nextSearch = searchInput.trim();
    setPage(1);
    if (nextSearch === search && page === 1) fetchUsers();
    else setSearch(nextSearch);
  };

  const togglePermission = async (user, permissionKey) => {
    const currentPermissions = (user.permissions || []).filter(permission => (
      ASSIGNABLE_PERMISSIONS.has(permission)
    ));
    const nextPermissions = currentPermissions.includes(permissionKey)
      ? currentPermissions.filter(permission => permission !== permissionKey)
      : [...currentPermissions, permissionKey];

    setRowMutation(user._id, true);
    try {
      const response = await apiClient.put(`/users/${user._id}/permissions`, {
        permissions: nextPermissions
      });
      replaceUser(response.data || { ...user, permissions: nextPermissions });
      toast.success(`Updated permissions for ${user.username}`);
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error(error.response?.data?.message || 'Failed to update permissions');
    } finally {
      setRowMutation(user._id, false);
    }
  };

  const changeStatus = async (user) => {
    const nextActive = !user.isActive;
    if (!nextActive && !window.confirm(`Deactivate ${user.username}? They will no longer be able to sign in.`)) {
      return;
    }

    setRowMutation(user._id, true);
    try {
      const response = await apiClient.patch(`/users/${user._id}/status`, { isActive: nextActive });
      replaceUser(response.data);
      toast.success(`${user.username} ${nextActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error(error.response?.data?.message || 'Failed to update account status');
    } finally {
      setRowMutation(user._id, false);
    }
  };

  const currentUserId = currentUser?._id || currentUser?.id || currentUser?.userId;

  return (
    <main className="min-h-full bg-surface-0 p-4 text-text-primary sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <header>
          <p className="text-sm font-medium text-accent">Admin settings</p>
          <h1 className="mt-1 text-2xl font-bold sm:text-3xl">People &amp; permissions</h1>
          <p className="mt-2 max-w-2xl text-sm text-text-secondary">
            Add team members, assign access, and reversibly activate or deactivate accounts.
          </p>
        </header>

        <section className="rounded-xl border border-glass-border bg-surface-1 p-4 sm:p-6" aria-labelledby="add-user-heading">
          <h2 id="add-user-heading" className="text-lg font-semibold">Add user</h2>
          <form onSubmit={handleCreate} className="mt-4 grid gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
            <div>
              <label htmlFor="new-user-username" className="block text-sm font-medium">Username</label>
              <input id="new-user-username" type="text" value={newUser.username} onChange={(event) => setNewUser({ ...newUser, username: event.target.value })} className="mt-1 min-h-11 w-full rounded-lg border border-glass-border bg-surface-0 px-3 text-text-primary" minLength={3} required />
            </div>
            <div>
              <label htmlFor="new-user-password" className="block text-sm font-medium">Temporary password</label>
              <input id="new-user-password" type="password" value={newUser.password} onChange={(event) => setNewUser({ ...newUser, password: event.target.value })} className="mt-1 min-h-11 w-full rounded-lg border border-glass-border bg-surface-0 px-3 text-text-primary" minLength={6} required />
            </div>
            <button type="submit" disabled={creating} className="flex min-h-11 items-center justify-center gap-2 rounded-lg bg-accent px-4 font-semibold text-black hover:bg-accent-hover disabled:opacity-50">
              <IconPlus className="h-5 w-5" aria-hidden="true" />
              {creating ? 'Adding…' : 'Add user'}
            </button>
          </form>
        </section>

        <section className="rounded-xl border border-glass-border bg-surface-1" aria-labelledby="users-heading">
          <div className="border-b border-glass-border p-4 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 id="users-heading" className="text-lg font-semibold">Users</h2>
                <p className="mt-1 text-sm text-text-secondary">{pagination.total} account{pagination.total === 1 ? '' : 's'}</p>
              </div>
              <form onSubmit={handleSearch} role="search" className="flex w-full gap-2 lg:max-w-md">
                <label htmlFor="user-search" className="sr-only">Search users</label>
                <input id="user-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search username or email" className="min-h-11 min-w-0 flex-1 rounded-lg border border-glass-border bg-surface-0 px-3 text-text-primary" />
                <button type="submit" className="flex min-h-11 min-w-11 items-center justify-center rounded-lg bg-accent px-3 font-semibold text-black hover:bg-accent-hover" aria-label="Search users">
                  <IconSearch className="h-5 w-5" aria-hidden="true" />
                </button>
              </form>
            </div>
          </div>

          {loadError && (
            <div role="alert" className="m-4 flex flex-col gap-3 rounded-lg border border-red-400/40 bg-red-400/10 p-4 sm:m-6 sm:flex-row sm:items-center sm:justify-between">
              <p>{loadError}</p>
              <button type="button" onClick={fetchUsers} className="min-h-11 rounded-lg bg-accent px-4 font-semibold text-black hover:bg-accent-hover">Retry</button>
            </div>
          )}

          {loadingUsers && users.length === 0 ? (
            <p role="status" className="p-8 text-center text-text-secondary">Loading users…</p>
          ) : users.length === 0 && !loadError ? (
            <div className="p-8 text-center"><h3 className="font-semibold">No users found</h3><p className="mt-1 text-sm text-text-secondary">Try another search or add a user above.</p></div>
          ) : (
            <>
              <div className="space-y-4 p-4 md:hidden" data-testid="mobile-user-list">
                {users.map(user => (
                  <article key={user._id} className="rounded-xl border border-glass-border bg-surface-0 p-4" aria-labelledby={`user-card-${user._id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0"><h3 id={`user-card-${user._id}`} className="truncate font-semibold">{user.username}</h3><p className="truncate text-sm text-text-secondary">{user.email || 'No email address'}</p></div>
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${user.isActive ? 'bg-green-400/15 text-green-300' : 'bg-surface-3 text-text-secondary'}`}>{user.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                    <dl className="my-4 grid grid-cols-2 gap-2 text-sm"><div><dt className="text-text-tertiary">Role</dt><dd className="capitalize">{user.role}</dd></div><div><dt className="text-text-tertiary">Created</dt><dd>{formatDate(user.createdAt)}</dd></div></dl>
                    <PermissionControls user={user} disabled={Boolean(mutatingUsers[user._id])} onToggle={togglePermission} />
                    <div className="mt-4 flex justify-end"><StatusControl user={user} currentUserId={currentUserId} disabled={Boolean(mutatingUsers[user._id])} onChange={changeStatus} /></div>
                  </article>
                ))}
              </div>

              <div className="hidden overflow-x-auto md:block" data-testid="desktop-user-list">
                <table className="min-w-full divide-y divide-glass-border">
                  <thead className="bg-surface-2 text-left text-xs uppercase tracking-wide text-text-tertiary"><tr><th scope="col" className="px-5 py-3">User</th><th scope="col" className="px-5 py-3">Status</th><th scope="col" className="min-w-[38rem] px-5 py-3">Permissions</th><th scope="col" className="px-5 py-3">Action</th></tr></thead>
                  <tbody className="divide-y divide-glass-border">
                    {users.map(user => (
                      <tr key={user._id} className={user.isActive ? '' : 'opacity-75'}>
                        <th scope="row" className="px-5 py-4 text-left align-top font-normal"><p className="font-semibold">{user.username}</p><p className="text-sm text-text-secondary">{user.email || 'No email address'}</p><p className="mt-1 text-xs capitalize text-text-tertiary">{user.role} · Added {formatDate(user.createdAt)}</p></th>
                        <td className="px-5 py-4 align-top text-sm">{user.isActive ? 'Active' : 'Inactive'}</td>
                        <td className="px-5 py-4 align-top"><PermissionControls user={user} disabled={Boolean(mutatingUsers[user._id])} onToggle={togglePermission} /></td>
                        <td className="px-5 py-4 align-top"><StatusControl user={user} currentUserId={currentUserId} disabled={Boolean(mutatingUsers[user._id])} onChange={changeStatus} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {users.length > 0 && (
            <nav className="flex items-center justify-between border-t border-glass-border p-4" aria-label="User list pagination">
              <button type="button" onClick={() => setPage(current => Math.max(current - 1, 1))} disabled={page <= 1 || loadingUsers} className="min-h-11 rounded-lg border border-glass-border px-4 font-medium disabled:opacity-50">Previous</button>
              <span className="text-sm text-text-secondary">Page {pagination.page} of {pagination.totalPages}</span>
              <button type="button" onClick={() => setPage(current => Math.min(current + 1, pagination.totalPages))} disabled={page >= pagination.totalPages || loadingUsers} className="min-h-11 rounded-lg border border-glass-border px-4 font-medium disabled:opacity-50">Next</button>
            </nav>
          )}
        </section>
      </div>
    </main>
  );
};

export default UserManagement;
